import axios, { isAxiosError } from 'axios';
import { getConfig } from '../config';

// Common interfaces
export interface MediaPublishResponse {
  success: boolean;
  message?: string;
}

export interface PublishImagesRequest {
  sku: string;
  toAdd?: string[];
  toDelete?: string[];
  clearAll?: boolean;
}

// Specific request types
export interface PublishFeaturedThumbnailRequest {
  sku: string;
  imageUrl: string;
}

// Type aliases for clarity
export type PublishFeaturedThumbnailResponse = MediaPublishResponse;
export type PublishGalleryImagesRequest = PublishImagesRequest;
export type PublishGalleryImagesResponse = MediaPublishResponse;
export type PublishLearnMoreImagesRequest = PublishImagesRequest;
export type PublishLearnMoreImagesResponse = MediaPublishResponse;

export class MediaClient {
  private readonly baseUrl: string;
  private readonly apiKey: string;

  constructor() {
    const config = getConfig();
    this.baseUrl = config.apiUrl;
    this.apiKey = config.apiKey;
  }

  async publishFeaturedThumbnail(request: PublishFeaturedThumbnailRequest): Promise<PublishFeaturedThumbnailResponse> {
    const url = `${this.baseUrl}/woocommerce/media/featured-thumbnail`;

    try {
      const response = await axios.post(url, request, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': this.apiKey,
        },
      });

      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      const errorMsg = isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : 'Unknown error occurred';

      console.error('Failed to publish featured thumbnail:', errorMsg);

      return {
        success: false,
        message: errorMsg,
      };
    }
  }

  async publishGalleryImages(request: PublishGalleryImagesRequest): Promise<PublishGalleryImagesResponse> {
    const url = `${this.baseUrl}/woocommerce/media/gallery-images`;

    try {
      const response = await axios.post(url, request, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': this.apiKey,
        },
      });

      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      const errorMsg = isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : 'Unknown error occurred';

      console.error('Failed to publish gallery images:', errorMsg);

      return {
        success: false,
        message: errorMsg,
      };
    }
  }

  async publishLearnMoreImages(request: PublishLearnMoreImagesRequest): Promise<PublishLearnMoreImagesResponse> {
    const url = `${this.baseUrl}/woocommerce/media/learn-more-images`;

    try {
      const response = await axios.post(url, request, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': this.apiKey,
        },
      });

      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      const errorMsg = isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : 'Unknown error occurred';

      console.error('Failed to publish learn more images:', errorMsg);

      return {
        success: false,
        message: errorMsg,
      };
    }
  }
}

export const mediaClient = new MediaClient();
